import type {CameraState,Vec3} from '../../shared';
import type {DuelBeat} from '../sim';
import {superPresentation,superStory,weaponMotion,isSuperAction as isWeaponSuper,type SuperAction} from '../../shared/weapon-motion';
import {duelTiming} from './duelPresentation';
import {abilityTime,abilityDuration} from './duelPlayback';
const ease=(v:number)=>{const x=Math.max(0,Math.min(1,v));return x*x*(3-2*x);};
const pulse=(t:number,a:number,b:number,c:number,d:number)=>ease((t-a)/(b-a))*(1-ease((t-c)/(d-c)));
const mix=(a:number,b:number,k:number)=>a+(b-a)*k;
export const SUPER_DASH=1.4;
export const techniqueBlend=(t:number,duration:number)=>ease(t/.25)*(1-ease((t-duration+.52)/.52));
/** Source run frame -> staged world, matching the rig's handedness conversion. */
export const sourceWorld=(p:readonly number[]):[number,number,number]=>[p[0]+.46,p[1],-p[2]];
/** Authored camera takes use the same stopped/slowed source clock as the rig.
 * The last take hands back to the ordinary arena framing. */
export interface ShotSubjects {head:Vec3;chest:Vec3;opponent?:Vec3;heroBounds?:{minY:number;maxY:number};aspect?:number;presentation?:ReturnType<typeof superPresentation>}
export function weaponShot(base:CameraState,beat:DuelBeat|null,ms:number,reduced:boolean,subjects?:ShotSubjects):CameraState {
 if(reduced||!beat||!beat.weapon||ms<0)return base;
 const t=isWeaponSuper(beat.action)?abilityTime(beat.action,ms/1000):ms/1000;
 if(isWeaponSuper(beat.action)) {
  const duration=weaponMotion(beat.weapon,beat.action).duration;
  if(ms/1000>=abilityDuration(beat.action))return base;
  if(beat.action!=='starfall'&&beat.action!=='thousandCuts')return abilityShot(base,beat.action,t,duration,subjects);
  const shot=(subjects?.presentation??superPresentation(beat.action,t,beat.weapon,SUPER_DASH)).camera;
  if(!shot||shot.weight<=0)return base;
  const eye=sourceWorld(shot.eye),target=sourceWorld(shot.target),s=superStory(beat.action,t);
  // Fixed source eyes/chest belong to its abstract actor. Keep the authored
  // take and lens, but aim the close shots at the actual equipped GLB anatomy.
  const closeHead=beat.action==='starfall'?s<.6:s<.45;
  const closeHands=beat.action==='starfall'?s>=3.83&&s<4.88:s>=1.45&&s<1.85;
  if(subjects&&(closeHead||closeHands)){
    const focus=closeHead?subjects.head:subjects.chest;
    for(let i=0;i<3;i++){const delta=focus[i]-target[i]+(i===1?(closeHead?-.045:.055):0);eye[i]+=delta;target[i]+=delta;}
  }
  return blendPerspective(base,{...base,eye,target,fovYDeg:shot.fov*180/Math.PI,dutchDeg:-shot.roll*180/Math.PI,orthoHalfH:0},shot.weight);
 }
 const timing=duelTiming(beat),first=timing.heroContact[0]/1000,last=timing.heroContact.at(-1)!/1000;
 if(beat.action==='guard') {
  const brace=pulse(t,0,.18,last+.04,last+.5);
  return {...base,target:[base.target[0]-.035*brace,base.target[1]+.025*brace,base.target[2]],orthoHalfH:base.orthoHalfH*(1-.025*brace)};
 }
 if(beat.action==='strike'||beat.action==='feint') {
  const windup=pulse(t,0,Math.max(.1,first-.15),last,last+.35);
  return {...base,orthoHalfH:base.orthoHalfH*(1-.04*windup),dutchDeg:(beat.weapon==='hammer'?-.8:.6)*windup};
 }
 return base;
}

/** These four skills are authored for this rig. Their cameras stay above the
 * courtyard, track actual equipped anatomy, and reserve the whole action pair
 * even when the leap or side pass exceeds the ordinary duel envelope. */
function abilityShot(base:CameraState,action:Exclude<SuperAction,'starfall'|'thousandCuts'>,t:number,duration:number,subjects?:ShotSubjects):CameraState {
 const hero=subjects?.chest??[-.75,.6,0],head=subjects?.head??[-.75,.95,0],foe=subjects?.opponent??[.46,.65,0];
 const weight=techniqueBlend(t,duration);
 // A reverse flip puts feet above the head; actual posed bounds must define
 // the envelope, not the upright assumption that the head is always highest.
 const top=Math.max(subjects?.heroBounds?.maxY??head[1]+.16,foe[1]+.4),bottom=Math.min(0,subjects?.heroBounds?.minY??hero[1]-.55);
 const focus:Vec3=[(hero[0]+foe[0])*.5,(top+bottom)*.5,(hero[2]+foe[2])*.5];
 let yaw=0,pitch=.18,roll=0,fov=34;
 if(action==='solarCleave'){
  const lift=pulse(t,.4,1.1,1.7,2.65);yaw=-.18+.48*ease(t/2.45);pitch=.12+.16*lift;roll=-2.2*lift;
 } else if(action==='seismicSlam'){
  // The top-down vault reveals the landing footprint; each wave stays on the
  // ground plane instead of becoming another large screen-facing attack ring.
  const vault=pulse(t,.5,1.3,1.8,2.45);yaw=.16;pitch=.24+.38*vault;fov=36;
 } else if(action==='tempest'){
  yaw=-.35+.7*ease(t/3.5);pitch=.22;roll=1.6*Math.sin(Math.min(t/3.32,1)*Math.PI);
 } else {
  // Hold the centre of all three passes. Following the attacking wrist would
  // whip the camera from side to side and lose the second dagger at 390px.
  yaw=-.12+.24*ease(t/3.25);pitch=.25;fov=38;
 }
 const aspect=Math.max(.6,subjects?.aspect??1),xSpan=Math.abs(hero[0]-foe[0])+1.05,zSpan=Math.abs(hero[2]-foe[2])+.85;
 const ySpan=top-bottom+.45;
 const halfH=Math.max(.86,ySpan*.58,(xSpan*Math.abs(Math.cos(yaw))+zSpan*Math.abs(Math.sin(yaw)))*.58/aspect);
 const dist=Math.max(2.5,halfH/Math.tan(fov*Math.PI/360));
 const eye:Vec3=[focus[0]+Math.sin(yaw)*Math.cos(pitch)*dist,focus[1]+Math.sin(pitch)*dist,focus[2]+Math.cos(yaw)*Math.cos(pitch)*dist];
 return blendPerspective(base,{...base,eye,target:focus,fovYDeg:fov,dutchDeg:roll,orthoHalfH:0},weight);
}

function blendPerspective(base:CameraState,shot:CameraState,k:number):CameraState {
 if(k>=1)return shot;
 const delta=(cam:CameraState)=>cam.eye.map((v,i)=>v-cam.target[i]) as Vec3;
 const a=delta(base),b=delta(shot),da=Math.hypot(...a),db=Math.hypot(...b);
 const target=base.target.map((v,i)=>mix(v,shot.target[i],k)) as Vec3;
 // Orbit the hand-back rather than linearly crossing through the actors.
 const ya=Math.atan2(a[0],a[2]),yb=Math.atan2(b[0],b[2]);
 const yaw=ya+Math.atan2(Math.sin(yb-ya),Math.cos(yb-ya))*k;
 const pitch=mix(Math.asin(a[1]/da),Math.asin(b[1]/db),k),dist=mix(da,db,k);
 const eye:Vec3=[target[0]+Math.sin(yaw)*Math.cos(pitch)*dist,target[1]+Math.sin(pitch)*dist,target[2]+Math.cos(yaw)*Math.cos(pitch)*dist];
 const fov=base.orthoHalfH>0?2*Math.atan(base.orthoHalfH/da)*180/Math.PI:base.fovYDeg;
 return {...base,eye,target,fovYDeg:mix(fov,shot.fovYDeg,k),dutchDeg:mix(base.dutchDeg,shot.dutchDeg,k),orthoHalfH:0};
}
